import type {
  AddAuditorFirmMemberPayload,
  ArchiveAuditDto,
  AuditArchiveJobResponse,
  AuditEntity,
  AuditEntityResponse,
  AuditorFirmMemberResponse,
  AuditRole,
  AuditRoleResponse,
  AuditRolesResponse,
  AuditsResponse,
  AuditParticipantsResponse,
  CreateAuditDto,
  InviteTeamMemberPayload,
  InvolvedCompanyMembersResponse,
  MyTeamResponse,
  StatusResponse,
  TeamMemberMeResponse,
  TeamMembersResponse,
  UpdateAuditDto,
  UpdateTeamMemberPayload,
} from '@/api';
import { ENDPOINTS } from '@/api/endpoints';
import { checkResponse } from '@/shared/api/contract/responseContract';
import API from '@api/API';
import {
  auditResponseSchema,
  teamMemberMeResponseSchema,
} from '@/shared/api/contract/audit.schema';

export const auditsApi = {
  getAudits: async (): Promise<AuditsResponse> => {
    const res = await API().get(ENDPOINTS.audits.list());
    return res.data;
  },

  getAudit: async (auditId: string | number): Promise<AuditEntityResponse> => {
    const res = await API().get(ENDPOINTS.audits.one(auditId));
    return checkResponse(auditResponseSchema, res.data, 'GET /audit/:id');
  },

  createAudit: async (payload: CreateAuditDto): Promise<AuditEntity> => {
    const res = await API().post(ENDPOINTS.audits.create(), payload);
    return res.data;
  },

  updateAudit: async (auditId: string, payload: UpdateAuditDto): Promise<AuditEntityResponse> => {
    const res = await API().post(ENDPOINTS.audits.update(auditId), payload);
    return res.data;
  },

  deleteAudit: async (auditId: string): Promise<void> => {
    const res = await API().delete(ENDPOINTS.audits.delete(auditId));
    return res.data;
  },

  /** Validates + freezes the audit and enqueues a background `archive` job. */
  archiveAudit: async (
    auditId: string | number,
    payload?: ArchiveAuditDto,
  ): Promise<AuditArchiveJobResponse & { message?: string }> => {
    const res = await API().post(ENDPOINTS.audits.archive(auditId), payload);
    return res.data;
  },

  /** Reads the background archive-job status for an audit. */
  archiveStatus: async (
    auditId: string | number,
    jobId?: number | null,
  ): Promise<AuditArchiveJobResponse | null> => {
    const res = await API().get<AuditArchiveJobResponse | null>(
      ENDPOINTS.audits.archiveStatus(auditId),
      jobId ? { params: { jobId } } : undefined,
    );
    return res.data;
  },

  getStatus: async (auditId: string | number): Promise<StatusResponse> => {
    const res = await API().get(ENDPOINTS.audits.status(auditId));
    return res.data;
  },

  getRoles: async (auditId: string | number): Promise<AuditRolesResponse> => {
    const res = await API().get(ENDPOINTS.audits.roles(auditId));
    return res.data;
  },

  getRole: async (
    auditId: string | number,
    roleId: string | number,
  ): Promise<AuditRoleResponse> => {
    const res = await API().get(ENDPOINTS.audits.rolesItem(auditId, roleId));
    return res.data;
  },

  createRole: async (auditId: string | number, payload: AuditRole): Promise<AuditRole> => {
    const res = await API().post(ENDPOINTS.audits.rolesCreate(auditId), payload);
    return res.data;
  },

  updateRole: async (
    auditId: string | number,
    roleId: string | number,
    payload: AuditRole,
  ): Promise<AuditRole> => {
    const res = await API().post(ENDPOINTS.audits.rolesUpdate(auditId, roleId), payload);
    return res.data;
  },

  deleteRole: async (auditId: string | number, roleId: string | number): Promise<void> => {
    const res = await API().delete(ENDPOINTS.audits.rolesItem(auditId, roleId));
    return res.data;
  },

  cancelArchivation: async (auditId: string | number): Promise<AuditEntity> => {
    const res = await API().post(ENDPOINTS.audits.cancelArchivation(auditId));
    return res.data;
  },

  /** Enqueues a background `download` job that generates the audit report. */
  generateReport: async (auditId: string | number): Promise<AuditArchiveJobResponse> => {
    const res = await API().post<AuditArchiveJobResponse>(ENDPOINTS.audits.generateReport(auditId));
    return res.data;
  },

  /** Reads the background report-generation job status for an audit. */
  generateReportStatus: async (
    auditId: string | number,
    jobId?: number | null,
  ): Promise<AuditArchiveJobResponse | null> => {
    const res = await API().get<AuditArchiveJobResponse | null>(
      ENDPOINTS.audits.generateReportStatus(auditId),
      jobId ? { params: { jobId } } : undefined,
    );
    return res.data;
  },

  getInvolvedCompanyMembers: async (
    auditId: string | number,
  ): Promise<InvolvedCompanyMembersResponse> => {
    const res = await API().get(ENDPOINTS.audits.involvedCompanyMembers(auditId));
    return res.data;
  },

  getMyTeam: async (auditId: string | number): Promise<MyTeamResponse> => {
    const res = await API().get(ENDPOINTS.audits.myTeam(auditId));
    return res.data;
  },

  getAuditorFirmMembers: async (auditId: string | number): Promise<AuditorFirmMemberResponse> => {
    const res = await API().get(ENDPOINTS.audits.auditorFirmMembers(auditId));
    return res.data;
  },

  addAuditorFirmMember: async (
    auditId: string | number,
    payload: AddAuditorFirmMemberPayload,
  ): Promise<void> => {
    const res = await API().post(ENDPOINTS.audits.addAuditorFirmMember(auditId), payload);
    return res.data;
  },

  removeAuditorFirmMember: async (
    auditId: string | number,
    memberId: string | number,
  ): Promise<void> => {
    const res = await API().delete(ENDPOINTS.audits.removeAuditorFirmMember(auditId, memberId));
    return res.data;
  },

  changeAuditorFirmMemberRole: async (
    auditId: string | number,
    memberId: string | number,
    payload: { role: string },
  ): Promise<void> => {
    const res = await API().post(
      ENDPOINTS.audits.changeAuditorFirmMemberRole(auditId, memberId),
      payload,
    );
    return res.data;
  },

  getTeamMembers: async (auditId: string | number): Promise<TeamMembersResponse> => {
    const res = await API().get(ENDPOINTS.audits.teamMembers(auditId));
    return res.data;
  },

  getParticipants: async (auditId: string | number): Promise<AuditParticipantsResponse> => {
    const res = await API().get(ENDPOINTS.audits.participants(auditId));
    return res.data;
  },

  deleteTeamMember: async (auditId: string | number, memberId: string | number): Promise<void> => {
    const res = await API().delete(ENDPOINTS.audits.teamMembersItem(auditId, memberId));
    return res.data;
  },

  getMyTeamRole: async (auditId: string | number): Promise<TeamMemberMeResponse> => {
    const res = await API().get(ENDPOINTS.audits.teamMe(auditId));
    return checkResponse(teamMemberMeResponseSchema, res.data, 'GET /audit/:id/team-members/me');
  },

  inviteTeamMember: async (
    auditId: string | number,
    payload: InviteTeamMemberPayload,
  ): Promise<unknown> => {
    const res = await API().post(ENDPOINTS.audits.inviteTeamMember(auditId), payload);
    return res.data;
  },

  updateTeamMember: async (
    auditId: string | number,
    memberId: string | number,
    payload: UpdateTeamMemberPayload,
  ): Promise<void> => {
    const res = await API().post(ENDPOINTS.audits.teamMembersUpdate(auditId, memberId), payload);
    return res.data;
  },
};
